import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AddGuestForm.scss";
import { useAuth } from "../Context/AuthContext";
import { MdDelete, MdCloudUpload, MdPeopleAlt } from "react-icons/md";
import {
  FaFileExcel,
  FaPlus,
  FaSave,
  FaUsers,
  FaCrown,
  FaChartPie,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { v4 as uuidv4 } from "uuid";
import {
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import * as XLSX from "xlsx";
import { useWeddingData } from "../hooks/useWeddingData";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import BlockDisplayComponent from "../BlockDisplayComponent/BlockDisplayComponent";

const REQUIRED_HEADERS = [
  "Family Name",
  "Guest Type",
  "Guest Name",
  "Taking Liquor",
];
const TRIAL_GUEST_LIMIT = 10;

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const isTrialUser = (data) => {
  return (
    data?.plan === "trial" ||
    data?.subscriptionPlan === "trial" ||
    data?.packageType === "trial" ||
    data?.isTrial === true
  );
};

const isPremiumUser = (data) => {
  return (
    data?.plan === "premium" ||
    data?.subscriptionPlan === "premium" ||
    data?.packageType === "premium" ||
    data?.isPremium === true
  );
};

const hasDigitalInviteAddon = (data) => {
  return data?.addons?.digitalInvite === true;
};

const canAccessDigitalInvite = (data) => {
  if (isTrialUser(data)) return true;
  if (isPremiumUser(data)) return hasDigitalInviteAddon(data);
  return false;
};

const AddGuestForm = () => {
  const { user } = useAuth();
  const { weddingData, loading: weddingLoading } = useWeddingData();
  const navigate = useNavigate();

  const [guestName, setGuestName] = useState("");
  const [takingLiquorInput, setTakingLiquorInput] = useState("No");
  const [familyName, setFamilyName] = useState("");
  const [guestType, setGuestType] = useState("");
  const [guestList, setGuestList] = useState([]);
  const [saveState, setSaveState] = useState(false);
  const [checkGuestCount, setCheckGuestCount] = useState(false);
  const [isGuestTypeLocked, setIsGuestTypeLocked] = useState(false);

  const [bulkMode, setBulkMode] = useState(false);
  const fileInputRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  const [statsLoading, setStatsLoading] = useState(true);
  const [existingGuestCount, setExistingGuestCount] = useState(0);
  const [savingFamily, setSavingFamily] = useState(false);
  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const bride_name = String(weddingData?.bride_name || "Bride").trim();
  const groom_name = String(weddingData?.groom_name || "Groom").trim();
  const siteSlug = weddingData?.slug || "";
  const hasAccess = canAccessDigitalInvite(weddingData);

  const isTrial = weddingData?.isTrial !== false;
  const planLabel = isTrial ? "Trial" : "Premium";

  const maxAllowedGuests = isTrial
    ? TRIAL_GUEST_LIMIT
    : Number.POSITIVE_INFINITY;

  const usedGuests = existingGuestCount;
  const remainingGuests = isTrial
    ? Math.max(0, maxAllowedGuests - usedGuests)
    : Number.POSITIVE_INFINITY;

  const isBusy = weddingLoading || statsLoading || bulkBusy || savingFamily;
  const canAddMoreGuests = !isTrial || remainingGuests > 0;

  const groomCollectionName = GROOM_COLLECTION;
  const brideCollectionName = BRIDE_COLLECTION;

  const functions = getFunctions(undefined, "us-central1");
  const saveGuestFamilySecure = httpsCallable(
    functions,
    "saveGuestFamilySecure"
  );

  useEffect(() => {
    const loadTheme = async () => {
      if (weddingLoading) return;

      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!weddingData?.slug) {
          setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", weddingData.slug));

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);
        setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
      } finally {
        setThemeLoading(false);
      }
    };

    loadTheme();
  }, [weddingData, weddingLoading]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const openUpgradeModal = (message) => {
    setUpgradeMessage(message);
    setShowUpgradeModal(true);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeMessage("");
  };

  const handleUpgradeNow = () => {
    closeUpgradeModal();
    navigate("/pricing");
  };

  const normalizeLiquor = (val) => {
    const s = String(val || "").trim().toLowerCase();
    return s === "yes" ? "Yes" : "No";
  };

  const normalizeGuestType = (raw) => {
    if (!raw) return "";
    const input = String(raw).trim();
    const lc = input.toLowerCase();

    if (lc === "g") return `${groom_name}'s Guest`;
    if (lc === "b") return `${bride_name}'s Guest`;

    if (input === `${groom_name}'s Guest`) return `${groom_name}'s Guest`;
    if (input === `${bride_name}'s Guest`) return `${bride_name}'s Guest`;

    const gName = groom_name.toLowerCase();
    const bName = bride_name.toLowerCase();

    if (lc.includes(gName) || lc.includes("groom")) {
      return `${groom_name}'s Guest`;
    }
    if (lc.includes(bName) || lc.includes("bride")) {
      return `${bride_name}'s Guest`;
    }

    return "";
  };

  const getCollectionNameForGuestType = (type) => {
    if (type === `${groom_name}'s Guest`) return GROOM_COLLECTION;
    if (type === `${bride_name}'s Guest`) return BRIDE_COLLECTION;
    return "";
  };

  const collectionForGuestType = (type) => {
    if (!user) return null;
    const collectionName = getCollectionNameForGuestType(type);
    if (!collectionName) return null;
    return collection(db, "users", user.uid, collectionName);
  };

  const publicCollectionForGuestType = (type) => {
    const collectionName = getCollectionNameForGuestType(type);
    if (!siteSlug || !collectionName) return null;
    return collection(db, "sites", siteSlug, collectionName);
  };

  const getTotalGuestCount = async () => {
    if (!user) return 0;

    let total = 0;

    const groomSnap = await getDocs(
      collection(db, "users", user.uid, groomCollectionName)
    );

    groomSnap.forEach((d) => {
      const arr = d.data()?.guests;
      if (Array.isArray(arr)) total += arr.length;
    });

    const brideSnap = await getDocs(
      collection(db, "users", user.uid, brideCollectionName)
    );

    brideSnap.forEach((d) => {
      const arr = d.data()?.guests;
      if (Array.isArray(arr)) total += arr.length;
    });

    return total;
  };

  const loadGuestStats = async () => {
    if (!user || weddingLoading || themeLoading || !siteTheme) return;

    try {
      setStatsLoading(true);
      const total = await getTotalGuestCount();
      setExistingGuestCount(total);
    } catch (error) {
      console.error("Failed to load guest stats:", error);
      setExistingGuestCount(0);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!weddingLoading && !themeLoading && siteTheme && user) {
      loadGuestStats();
    }
  }, [weddingLoading, themeLoading, siteTheme, user]);

  const refreshStatsAfterWrite = async () => {
    await loadGuestStats();
  };

  const ensureGuestCapacity = async (incomingCount = 1) => {
    const currentCount = await getTotalGuestCount();
    const nextCount = currentCount + incomingCount;

    if (!isTrial) {
      return { allowed: true, currentCount };
    }

    if (nextCount > TRIAL_GUEST_LIMIT) {
      setCheckGuestCount(true);
      openUpgradeModal(
        `Your trial plan allows only ${TRIAL_GUEST_LIMIT} guests. Need to add more? Upgrade to the premium version.`
      );
      return { allowed: false, currentCount };
    }

    setCheckGuestCount(false);
    return { allowed: true, currentCount };
  };

  const handleAddGuest = async () => {
    if (!guestName.trim() || !guestType) {
      alert("Please enter both Guest Name and Guest Type.");
      return;
    }

    if (statsLoading || weddingLoading) {
      return;
    }

    const incomingDraftCount = guestList.length + 1;
    const { allowed, currentCount } = await ensureGuestCapacity(
      incomingDraftCount
    );

    if (!allowed) {
      return;
    }

    if (isTrial && currentCount + guestList.length + 1 > TRIAL_GUEST_LIMIT) {
      setCheckGuestCount(true);
      openUpgradeModal(
        `Your trial plan allows only ${TRIAL_GUEST_LIMIT} guests. Need to add more? Upgrade to the premium version.`
      );
      return;
    }

    setGuestList((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name: guestName.trim(),
        status: "Pending",
        takingLiquor: takingLiquorInput,
      },
    ]);

    setGuestName("");
    setTakingLiquorInput("No");
    setIsGuestTypeLocked(true);
  };

  const handleDeleteGuest = (index) => {
    const updated = guestList.filter((_, i) => i !== index);
    setGuestList(updated);

    if (updated.length === 0) {
      setIsGuestTypeLocked(false);
    }
  };

  const handleLiquorChange = (index, value) => {
    setGuestList((prev) => {
      const next = [...prev];
      next[index].takingLiquor = value;
      return next;
    });
  };

  const handleSaveFamily = async () => {
    if (!user) {
      alert("Please log in first.");
      return;
    }

    if (!siteSlug) {
      alert("Wedding slug not found. Please refresh and try again.");
      return;
    }

    if (!familyName.trim()) {
      alert("Please enter a Family Name.");
      return;
    }

    if (!guestType) {
      alert("Please select a Guest Type.");
      return;
    }

    if (guestList.length === 0) {
      alert("Please add at least one guest before saving.");
      return;
    }

    try {
      setSavingFamily(true);

      await saveGuestFamilySecure({
        slug: siteSlug,
        familyName: familyName.trim(),
        guestType,
        guests: guestList.map((guest) => ({
          id: guest.id || uuidv4(),
          name: String(guest.name || "").trim(),
          status: guest.status || "Pending",
          takingLiquor: normalizeLiquor(guest.takingLiquor),
        })),
      });

      setSaveState(true);
      setFamilyName("");
      setGuestType("");
      setGuestList([]);
      setIsGuestTypeLocked(false);
      setGuestName("");
      setTakingLiquorInput("No");
      setCheckGuestCount(false);

      await refreshStatsAfterWrite();

      setTimeout(() => setSaveState(false), 1600);
    } catch (err) {
      console.error("Error saving guests:", err);

      const code = err?.code || "";
      const message = err?.message || "Error saving guests. Please try again.";

      if (
        code.includes("failed-precondition") &&
        message.toLowerCase().includes("trial plan allows only")
      ) {
        setCheckGuestCount(true);
        openUpgradeModal(
          `You can only save up to ${TRIAL_GUEST_LIMIT} guests on the trial plan. Need more? Upgrade to premium.`
        );
        return;
      }

      if (code.includes("already-exists")) {
        alert(
          `A family named "${familyName.trim()}" already exists for this guest type.`
        );
        return;
      }

      if (code.includes("permission-denied")) {
        alert("You do not have permission to save guests for this wedding.");
        return;
      }

      alert(message);
    } finally {
      setSavingFamily(false);
    }
  };

  const triggerFileDialog = () => fileInputRef.current?.click();

  const handleDownloadTemplate = () => {
    const rows = [
      {
        "Family Name": "Fernando",
        "Guest Type": "G",
        "Guest Name": "Kasun Fernando",
        "Taking Liquor": "Yes",
      },
      {
        "Family Name": "Fernando",
        "Guest Type": "G",
        "Guest Name": "Ishara Fernando",
        "Taking Liquor": "No",
      },
      {
        "Family Name": "Perera",
        "Guest Type": "B",
        "Guest Name": "Nimali Perera",
        "Taking Liquor": "No",
      },
      {
        "Family Name": "Perera",
        "Guest Type": "B",
        "Guest Name": "Suresh Perera",
        "Taking Liquor": "Yes",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(rows, { header: REQUIRED_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Guests");
    XLSX.writeFile(wb, "guest_bulk_template.xlsx");
  };

  const onBulkFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!siteSlug) {
      setBulkMsg("Wedding slug not found. Please refresh and try again.");
      return;
    }

    setBulkMsg("");
    setBulkBusy(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const rowCount = rows.length;

      if (rowCount === 0) {
        setBulkMsg("No records found in the file.");
        setBulkBusy(false);
        return;
      }

      const firstRow = rows[0] || {};
      const headers = Object.keys(firstRow);
      const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));

      if (missing.length) {
        setBulkMsg(
          `Invalid file. Missing column(s): ${missing.join(
            ", "
          )}. Expected columns: ${REQUIRED_HEADERS.join(", ")}`
        );
        setBulkBusy(false);
        return;
      }

      const existingCount = await getTotalGuestCount();

      if (isTrial && existingCount + rowCount > TRIAL_GUEST_LIMIT) {
        setBulkMsg(
          `You already used ${existingCount} guest slots. Trial plan allows only ${TRIAL_GUEST_LIMIT} guests in total.`
        );
        openUpgradeModal(
          `Your file contains ${rowCount} guests, but you only have ${Math.max(
            0,
            TRIAL_GUEST_LIMIT - existingCount
          )} remaining guest slots on the trial plan. Upgrade to premium to continue.`
        );
        setBulkBusy(false);
        return;
      }

      const groups = new Map();

      for (const raw of rows) {
        const fam = String(raw["Family Name"] || "").trim();
        const gtype = normalizeGuestType(raw["Guest Type"]);
        const gname = String(raw["Guest Name"] || "").trim();
        const liquor = normalizeLiquor(raw["Taking Liquor"]);

        if (!fam || !gtype || !gname) continue;

        const key = `${gtype}|||${fam}`;

        if (!groups.has(key)) {
          groups.set(key, {
            familyName: fam,
            guestType: gtype,
            guests: [],
          });
        }

        groups.get(key).guests.push({
          id: uuidv4(),
          name: gname,
          status: "Pending",
          takingLiquor: liquor,
        });
      }

      let createdCount = 0;
      let mergedGuestCount = 0;

      for (const [, group] of groups) {
        const privateCol = collectionForGuestType(group.guestType);
        const publicCol = publicCollectionForGuestType(group.guestType);

        if (!privateCol || !publicCol) continue;

        const qFam = query(
          privateCol,
          where("familyName", "==", group.familyName),
          where("guestType", "==", group.guestType)
        );

        const snap = await getDocs(qFam);

        if (snap.empty) {
          const payload = {
            familyName: group.familyName,
            guestType: group.guestType,
            guests: group.guests,
            createdBy: user?.email || "unknown",
            createdAt: serverTimestamp(),
          };

          const newPrivateRef = doc(privateCol);
          const newPublicRef = doc(
            db,
            "sites",
            siteSlug,
            privateCol.id,
            newPrivateRef.id
          );

          await setDoc(newPrivateRef, payload);
          await setDoc(newPublicRef, payload);

          createdCount += group.guests.length;
        } else {
          const d = snap.docs[0];
          const privateRef = doc(db, "users", user.uid, privateCol.id, d.id);
          const publicRef = doc(db, "sites", siteSlug, privateCol.id, d.id);

          const existing = d.data() || {};
          const existingGuests = Array.isArray(existing.guests)
            ? existing.guests
            : [];

          const existingNames = new Set(
            existingGuests.map((g) =>
              String(g.name || "")
                .trim()
                .toLowerCase()
            )
          );

          const toAppend = group.guests.filter(
            (g) =>
              !existingNames.has(
                String(g.name || "")
                  .trim()
                  .toLowerCase()
              )
          );

          if (
            isTrial &&
            existingCount +
            createdCount +
            mergedGuestCount +
            toAppend.length >
            TRIAL_GUEST_LIMIT
          ) {
            setBulkMsg(
              `Trial guest limit reached at ${TRIAL_GUEST_LIMIT}. Upgrade to premium to continue.`
            );
            openUpgradeModal(
              `You have reached the trial limit of ${TRIAL_GUEST_LIMIT} guests. Upgrade to premium to continue adding guests.`
            );
            setBulkBusy(false);
            return;
          }

          if (toAppend.length > 0) {
            const updatedGuests = [...existingGuests, ...toAppend];

            await updateDoc(privateRef, { guests: updatedGuests });

            const publicSnap = await getDoc(publicRef);
            if (publicSnap.exists()) {
              await updateDoc(publicRef, { guests: updatedGuests });
            } else {
              await setDoc(publicRef, {
                familyName: group.familyName,
                guestType: group.guestType,
                guests: updatedGuests,
                createdBy: user?.email || "unknown",
                createdAt: existing.createdAt || serverTimestamp(),
              });
            }

            mergedGuestCount += toAppend.length;
          }
        }
      }

      setBulkMsg(
        `Upload complete. Added ${createdCount + mergedGuestCount} guests successfully.`
      );

      await refreshStatsAfterWrite();

      e.target.value = "";
    } catch (err) {
      console.error("Bulk upload failed:", err);
      setBulkMsg("Bulk upload failed. Please check your file and try again.");
    } finally {
      setBulkBusy(false);
    }
  };

  const clearForm = () => {
    setFamilyName("");
    setGuestType("");
    setGuestList([]);
    setIsGuestTypeLocked(false);
    setGuestName("");
    setTakingLiquorInput("No");
    setCheckGuestCount(false);
  };



  if (!user || weddingLoading || themeLoading || !siteTheme) return null;

  if (!hasAccess) {
    return (
      <BlockDisplayComponent
        title="This feature is locked"
        description="Premium users can access Add Guests only when the Digital Invite add-on has been added to their package. Call 070 536 7980 to get this feature."
        primaryButtonText="Add this feature"
        secondaryButtonText="Go Back"
        onPrimaryClick={() => navigate("/premium")}
        onSecondaryClick={() => navigate(-1)}
      />
    );
  }

  return (
    <>
      <section className="guestForm" aria-label="Add guests" style={themeStyle}>
        <div className="guestForm__hero">
          <div className="guestForm__heroText">
            <p className="guestForm__eyebrow">Guest Management</p>
            <h1 className="guestForm__title">Add Guests</h1>
            <p className="guestForm__subtitle">
              Add guests manually or import them in bulk. Your plan details and
              remaining guest count are shown on the left.
            </p>
          </div>
        </div>

        <div className="guestForm__layout">
          <aside className="guestForm__sideCard">
            <div className="guestForm__sideHead">
              <span className="guestForm__sideIcon">
                <MdPeopleAlt />
              </span>
              <div>
                <h3 className="guestForm__sideTitle">Guest Setup</h3>
                <p className="guestForm__sideText">
                  Choose how you want to add your invitation list.
                </p>
              </div>
            </div>

            <div className="guestForm__planCard">
              <div className="guestForm__planTop">
                <span className="guestForm__summaryIcon">
                  {isTrial ? <FaChartPie /> : <FaCrown />}
                </span>

                <div>
                  <p className="guestForm__summaryLabel">Current Plan</p>
                  <h4 className="guestForm__summaryValue">{planLabel}</h4>
                </div>
              </div>

              {isTrial ? (
                <p className="guestForm__planText">
                  Trial users can add up to {TRIAL_GUEST_LIMIT} guests.
                </p>
              ) : (
                <p className="guestForm__planText">
                  Premium users can add unlimited guests.
                </p>
              )}
            </div>

            <div
              className="guestForm__switchWrap"
              role="group"
              aria-label="Bulk upload toggle"
            >
              <div className="guestForm__switchCopy">
                <span className="guestForm__switchLabel">Bulk Upload</span>
                <p className="guestForm__switchHelp">
                  Turn on to upload Excel or CSV files instead of adding guests
                  one by one.
                </p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={bulkMode}
                className={`guestForm__switch ${bulkMode ? "is-on" : ""}`}
                onClick={() => setBulkMode((v) => !v)}
                disabled={isBusy}
              >
                <span className="guestForm__switchThumb" />
              </button>
            </div>

            <div className="guestForm__summary">
              <div className="guestForm__summaryCard">
                <span className="guestForm__summaryIcon">
                  <FaUsers />
                </span>
                <div>
                  <p className="guestForm__summaryLabel">Allowed Guests</p>
                  <h4 className="guestForm__summaryValue">
                    {statsLoading
                      ? "Loading..."
                      : isTrial
                        ? TRIAL_GUEST_LIMIT
                        : "Unlimited"}
                  </h4>
                </div>
              </div>

              <div className="guestForm__summaryCard">
                <span className="guestForm__summaryIcon">
                  <FaPlus />
                </span>
                <div>
                  <p className="guestForm__summaryLabel">
                    Remaining Allowed Guests
                  </p>
                  <h4 className="guestForm__summaryValue">
                    {statsLoading
                      ? "Loading..."
                      : isTrial
                        ? remainingGuests
                        : "Unlimited"}
                  </h4>
                </div>
              </div>
            </div>

            {isTrial && !statsLoading && remainingGuests <= 0 && (
              <div className="guestForm__limitBox">
                <p className="guestForm__limitTitle">Trial limit reached</p>
                <p className="guestForm__limitText">
                  Need to add more guests? Contact <b>070 536 7980</b> Upgrade
                  to the premium version.
                </p>
              </div>
            )}

            <div className="guestForm__tipBox">
              <h4 className="guestForm__tipTitle">Tips</h4>
              <ul className="guestForm__tips">
                <li>Group guests by family for easier RSVP handling.</li>
                <li>Use bulk upload for large guest lists.</li>
                <li>
                  Guest type locks after the first added guest in the same
                  family.
                </li>
              </ul>
            </div>
          </aside>

          <div className="guestForm__main">
            {bulkMode ? (
              <div className="guestForm__panel">
                <div className="guestForm__panelHead">
                  <div>
                    <p className="guestForm__panelEyebrow">Bulk Import</p>
                    <h2 className="guestForm__panelTitle">Upload Guest File</h2>
                  </div>
                </div>

                <div className="guestForm__bulkCard">
                  <div className="guestForm__bulkIntro">
                    <span className="guestForm__bulkIcon">
                      <MdCloudUpload />
                    </span>
                    <div>
                      <h3 className="guestForm__bulkTitle">
                        Import from Excel or CSV
                      </h3>
                      <p className="guestForm__bulkText">
                        Your file must follow the correct structure before
                        upload.
                      </p>
                    </div>
                  </div>

                  <ol className="guestForm__instructions">
                    <li>
                      File must contain exactly <strong>4 columns</strong>:
                      Family Name, Guest Type, Guest Name and Taking Liquor.
                    </li>
                    <li>
                      Taking Liquor should be either <strong>Yes</strong> or{" "}
                      <strong>No</strong>.
                    </li>
                    <li>
                      Guest Type should be <strong>G</strong> for groom’s side
                      and <strong>B</strong> for bride’s side.
                    </li>
                    <li>
                      Download the ready-made template below before preparing
                      your file.
                    </li>
                  </ol>

                  <div className="guestForm__bulkActions">
                    <ButtonComponent
                      className="is-ghost guestForm__actionBtn"
                      onClick={handleDownloadTemplate}
                      disabled={isBusy}
                    >
                      <FaFileExcel style={{ marginRight: 8 }} />
                      Download Template
                    </ButtonComponent>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      style={{ display: "none" }}
                      onChange={onBulkFileSelected}
                    />

                    <ButtonComponent
                      onClick={triggerFileDialog}
                      disabled={isBusy || (isTrial && remainingGuests <= 0)}
                      className={`guestForm__actionBtn ${bulkBusy ? "is-busy" : ""
                        }`}
                    >
                      <MdCloudUpload style={{ marginRight: 8 }} />
                      {bulkBusy ? "Uploading..." : "Upload Guest File"}
                    </ButtonComponent>
                  </div>

                  {bulkMsg && (
                    <p
                      className="guestForm__feedback"
                      role="status"
                      aria-live="polite"
                    >
                      {bulkMsg}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="guestForm__panel">
                  <div className="guestForm__panelHead">
                    <div>
                      <p className="guestForm__panelEyebrow">Manual Entry</p>
                      <h2 className="guestForm__panelTitle">
                        Create Guest Family
                      </h2>
                    </div>
                  </div>

                  <div className="guestForm__grid">
                    <div className="guestForm__group">
                      <label className="guestForm__label">Family Name</label>
                      <input
                        type="text"
                        className="guestForm__input"
                        value={familyName}
                        onChange={(e) => setFamilyName(e.target.value)}
                        placeholder="Enter Family Name"
                        disabled={isBusy || !canAddMoreGuests}
                        autoCorrect="off"
                      />
                    </div>

                    <div className="guestForm__group">
                      <label className="guestForm__label">Guest Type</label>
                      <select
                        className="guestForm__input"
                        value={guestType}
                        onChange={(e) => setGuestType(e.target.value)}
                        disabled={
                          isGuestTypeLocked || isBusy || !canAddMoreGuests
                        }
                      >
                        <option value="" disabled>
                          Select Guest Type
                        </option>
                        <option value={`${groom_name}'s Guest`}>
                          {`${groom_name}'s Guest`}
                        </option>
                        <option value={`${bride_name}'s Guest`}>
                          {`${bride_name}'s Guest`}
                        </option>
                      </select>
                    </div>

                    <div className="guestForm__group guestForm__group--full">
                      <label className="guestForm__label">Guest Name</label>
                      <input
                        type="text"
                        className="guestForm__input"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Enter Guest Name"
                        disabled={isBusy || !canAddMoreGuests}
                        autoCorrect="off"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddGuest();
                          }
                        }}
                      />
                    </div>

                    <div className="guestForm__group guestForm__group--full">
                      <label className="guestForm__label">Taking Liquor</label>
                      <select
                        className="guestForm__select"
                        value={takingLiquorInput}
                        onChange={(e) => setTakingLiquorInput(e.target.value)}
                        disabled={isBusy || !canAddMoreGuests}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                  </div>

                  <div className="guestForm__inlineActions">
                    <ButtonComponent
                      className="guestForm__actionBtn"
                      onClick={handleAddGuest}
                      disabled={isBusy || !canAddMoreGuests}
                    >
                      <FaPlus style={{ marginRight: 8 }} />
                      Add Guest
                    </ButtonComponent>
                  </div>

                  {checkGuestCount && (
                    <div className="guestForm__warning">
                      Trial plan limit reached. You can only add up to{" "}
                      {TRIAL_GUEST_LIMIT} guests.
                    </div>
                  )}
                </div>

                <div className="guestForm__panel">
                  <div className="guestForm__panelHead">
                    <div>
                      <p className="guestForm__panelEyebrow">Preview</p>
                      <h2 className="guestForm__panelTitle">Guest Draft</h2>
                    </div>
                  </div>

                  {guestList.length > 0 ? (
                    <div
                      className="guestForm__preview"
                      role="region"
                      aria-live="polite"
                    >
                      <div className="guestForm__previewTop">
                        <h3 className="guestForm__previewHead">
                          Guests in this family
                        </h3>
                        <span className="guestForm__previewCount">
                          {guestList.length}
                        </span>
                      </div>

                      <ul className="guestForm__list">
                        {guestList.map((g, i) => (
                          <li key={g.id} className="guestForm__item">
                            <div className="guestForm__itemTop">
                              <div className="guestForm__itemText">
                                <span className="guestForm__name">{g.name}</span>
                                <p className="guestForm__meta">
                                  Status: {g.status}
                                </p>
                              </div>

                              <ButtonComponent
                                className="is-ghost icon"
                                onClick={() => handleDeleteGuest(i)}
                                aria-label={`Remove ${g.name}`}
                                title="Remove"
                                disabled={isBusy}
                              >
                                <MdDelete />
                              </ButtonComponent>
                            </div>

                            <div className="guestForm__fieldRow">
                              <label
                                htmlFor={`liquor-${g.id}`}
                                className="guestForm__labelSmall"
                              >
                                Taking Liquor
                              </label>
                              <select
                                id={`liquor-${g.id}`}
                                className="guestForm__select"
                                value={g.takingLiquor}
                                onChange={(e) =>
                                  handleLiquorChange(i, e.target.value)
                                }
                                disabled={isBusy}
                              >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                              </select>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="guestForm__emptyState">
                      Start by adding a guest to build this family list.
                    </div>
                  )}

                  <div className="guestForm__actions">
                    <ButtonComponent
                      className="is-ghost guestForm__actionBtn"
                      onClick={clearForm}
                      disabled={isBusy}
                    >
                      Clear
                    </ButtonComponent>

                    <ButtonComponent
                      className="guestForm__actionBtn"
                      onClick={handleSaveFamily}
                      disabled={isBusy || guestList.length === 0}
                    >
                      <FaSave style={{ marginRight: 8 }} />
                      {savingFamily ? "Saving..." : "Save Family"}
                    </ButtonComponent>
                  </div>

                  {saveState && (
                    <p className="guestForm__saved">
                      Guests saved successfully.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {showUpgradeModal && (
        <div className="guestUpgradeModal" onClick={closeUpgradeModal}>
          <div
            className="guestUpgradeModal__card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            style={themeStyle}
          >
            <div className="guestUpgradeModal__icon">
              <FaCrown />
            </div>

            <h3 id="upgrade-modal-title" className="guestUpgradeModal__title">
              Upgrade to Premium
            </h3>

            <p className="guestUpgradeModal__text">{upgradeMessage}</p>

            <div className="guestUpgradeModal__actions">
              <ButtonComponent
                className="is-ghost guestUpgradeModal__btn"
                onClick={closeUpgradeModal}
              >
                Maybe Later
              </ButtonComponent>

              <ButtonComponent
                className="guestUpgradeModal__btn"
                onClick={handleUpgradeNow}
              >
                Upgrade Now
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddGuestForm;